/**
 * SCK-706 P0 — public /onboarding landing page.
 *
 * Users land here when helpers/actions.js catches a
 * 403 {"error":"no_membership"} from coord (signed in, but not
 * attached to any account). The page gives them three ways forward:
 *
 *   1. Sign out — if they used the wrong Google account. This is the
 *      most common cause; showing it prominently prevents most support
 *      loops.
 *   2. Redeem an invitation — paste the link/token from an email.
 *   3. Request access — POST /api/invitations/request so an operator
 *      can attach them to a workspace.
 *
 * Uses raw fetch (NOT helpers/actions.js) so the interceptor's own
 * 403 → /onboarding redirect cannot recurse on this page's own calls.
 * Route is mounted OUTSIDE ProtectedRoutes / PermissionsProvider in
 * App.js so opening /onboarding never fires /api/me/permissions —
 * which would 403 no_membership and loop.
 */

import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";

import { API_URL } from "../helpers/constants";
import { getToken, clearToken } from "../helpers/tokenStorage";

// Read the signed-in user's email from the JWT.
const emailFromToken = () => {
  try {
    const token = getToken();
    if (!token) return "";
    const claims = jwtDecode(token) || {};
    return (
      claims.email ||
      claims.preferred_username ||
      claims.sub ||
      ""
    );
  } catch {
    return "";
  }
};

// Accept a full redemption URL or a bare token. Handle cross-origin
// URLs explicitly — a link pasted from a staging environment must not
// be silently rewritten to the current origin.
const parseInvitationInput = (raw) => {
  const trimmed = (raw || "").trim();
  if (!trimmed) return null;

  try {
    const asUrl = new URL(trimmed);

    // Different environment — hand off with a full-page assign so the
    // token reaches the host it was issued for.
    if (asUrl.origin !== window.location.origin) {
      return { kind: "external", href: asUrl.href };
    }

    const tokenParam = asUrl.searchParams.get("token");
    if (tokenParam) {
      return { kind: "internal", path: `/redeem?token=${encodeURIComponent(tokenParam)}` };
    }
    const match = asUrl.pathname.match(/\/invitations\/([^/?#]+)/i);
    if (match) {
      return { kind: "internal", path: `/redeem?token=${encodeURIComponent(match[1])}` };
    }
    if (/\/(redeem|invitations|accept-nda)(\/|$|\?)/i.test(asUrl.pathname + asUrl.search)) {
      return { kind: "internal", path: asUrl.pathname + asUrl.search };
    }
    return null;
  } catch {
    if (/^[A-Za-z0-9_.\-]{16,}$/.test(trimmed)) {
      return { kind: "internal", path: `/redeem?token=${encodeURIComponent(trimmed)}` };
    }
    return null;
  }
};

export default function OnboardingPage() {
  const navigate = useNavigate();
  const initialEmail = useMemo(() => emailFromToken(), []);
  const signedInEmail = initialEmail;

  const [inviteInput, setInviteInput] = useState("");
  const [inviteError, setInviteError] = useState(null);

  const [email, setEmail] = useState(initialEmail);
  const [motivation, setMotivation] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [requestError, setRequestError] = useState(null);
  const [requestDone, setRequestDone] = useState(false);

  const handleSignOut = () => {
    try {
      clearToken();
    } catch {
      /* fall through — /login will handle a stale token */
    }
    window.location.assign("/login");
  };

  const handleUseInvitation = (e) => {
    e?.preventDefault?.();
    setInviteError(null);
    const parsed = parseInvitationInput(inviteInput);
    if (!parsed) {
      setInviteError(
        "Expected a link like https://console.clue2.app/redeem?token=… — paste the full URL from your invitation email."
      );
      return;
    }
    if (parsed.kind === "external") {
      window.location.assign(parsed.href);
      return;
    }
    navigate(parsed.path, { replace: false });
  };

  const handleRequestAccess = async (e) => {
    e?.preventDefault?.();
    setRequestError(null);

    if (!email) {
      setRequestError("Please enter the email address you use to sign in.");
      return;
    }

    setRequesting(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/invitations/request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ email, motivation: motivation || "" }),
      });
      if (!res.ok && res.status !== 202 && res.status !== 204) {
        let msg = `Request failed (HTTP ${res.status})`;
        try {
          const data = await res.json();
          if (data?.message) msg = data.message;
        } catch {
          /* keep default */
        }
        throw new Error(msg);
      }
      setRequestDone(true);
    } catch (err) {
      setRequestError(err.message || "Failed to submit request. Please try again.");
    } finally {
      setRequesting(false);
    }
  };

  return (
    <Card sx={{ width: "min(720px, 92vw)", boxShadow: "none" }}>
      <CardContent>
        <Typography variant="h5" component="h1" gutterBottom>
          Access required
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          You're signed in, but you haven't been added to a workspace yet.
          Redeem an invitation, or request access below.
        </Typography>

        {/* Identity + sign-out — resolves the "wrong Google account" case */}
        {signedInEmail && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "flex-start", sm: "center" }}
            sx={{ mt: 2, mb: 1 }}
          >
            <Chip
              size="small"
              label={`Signed in as ${signedInEmail}`}
              data-testid="onboarding-signed-in-as"
              sx={{ maxWidth: "100%" }}
            />
            <Button
              size="small"
              variant="text"
              startIcon={<LogoutIcon fontSize="small" />}
              onClick={handleSignOut}
              data-testid="onboarding-signout"
            >
              Not you? Sign out and try another account
            </Button>
          </Stack>
        )}

        {requestDone ? (
          <>
            {/* Terminal success state — give the user real next steps
                so /onboarding doesn't become a cul-de-sac */}
            <Alert
              severity="success"
              sx={{ mt: 2 }}
              data-testid="onboarding-request-success"
            >
              Request received. We'll email you at <strong>{email}</strong>{" "}
              once an admin has added you to a workspace. You can close
              this tab — or check back later.
            </Alert>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<LogoutIcon fontSize="small" />}
                onClick={handleSignOut}
                data-testid="onboarding-request-signout"
              >
                Sign out
              </Button>
              <Button
                variant="text"
                href="https://clue2app.ai"
                data-testid="onboarding-request-marketing"
              >
                Back to clue2app.ai
              </Button>
            </Stack>
          </>
        ) : (
          <>
            {/* Path 1 — paste an invitation link */}
            <Box
              component="form"
              onSubmit={handleUseInvitation}
              sx={{ mt: 2 }}
              data-testid="onboarding-invitation-form"
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Redeem an invitation
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Paste the full URL from your invitation email.
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="https://console.clue2.app/redeem?token=..."
                value={inviteInput}
                onChange={(e) => {
                  setInviteInput(e.target.value);
                  if (inviteError) setInviteError(null);
                }}
                inputProps={{ "data-testid": "onboarding-invitation-input" }}
              />
              {inviteError && (
                <Alert
                  severity="error"
                  sx={{ mt: 1 }}
                  data-testid="onboarding-invitation-error"
                >
                  {inviteError}
                </Alert>
              )}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!inviteInput.trim()}
                  data-testid="onboarding-invitation-submit"
                >
                  Continue
                </Button>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }}>or</Divider>

            {/* Path 2 — request access */}
            <Box
              component="form"
              onSubmit={handleRequestAccess}
              data-testid="onboarding-request-form"
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Request access to a workspace
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Tell us which team or company you're joining. An admin
                will attach you to the right workspace and email you when
                you're set up.
              </Typography>

              <TextField
                fullWidth
                size="small"
                type="email"
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                helperText="Access is granted to the account you're signed in with."
                sx={{ mt: 1 }}
                inputProps={{ "data-testid": "onboarding-request-email" }}
              />
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={3}
                label="Message (optional)"
                placeholder="Team or company name, and who invited you if you know."
                value={motivation}
                onChange={(e) => setMotivation(e.target.value)}
                sx={{ mt: 2 }}
                inputProps={{ "data-testid": "onboarding-request-motivation" }}
              />
              {requestError && (
                <Alert
                  severity="error"
                  sx={{ mt: 2 }}
                  data-testid="onboarding-request-error"
                >
                  {requestError}
                </Alert>
              )}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button
                  type="submit"
                  variant="outlined"
                  disabled={requesting || !email}
                  data-testid="onboarding-request-submit"
                  startIcon={
                    requesting ? <CircularProgress size={16} /> : null
                  }
                >
                  {requesting ? "Sending…" : "Send request"}
                </Button>
              </Box>
            </Box>
          </>
        )}
      </CardContent>

      <CardActions sx={{ justifyContent: "flex-start", p: 2, pt: 0 }}>
        <Typography variant="caption" color="text.secondary">
          Need help?{" "}
          <MuiLink
            href="mailto:support@clue2app.ai"
            data-testid="onboarding-support-link"
          >
            Contact support
          </MuiLink>
          .
        </Typography>
      </CardActions>
    </Card>
  );
}
